import React, { useState, useEffect } from 'react'
import Grid from '@material-ui/core/Grid'
import TextField from '@material-ui/core/TextField'
import {
  Select,
  MenuItem,
  InputLabel,
  InputAdornment,
  FormControl,
  Typography,
} from '@material-ui/core'
import { MultiSelect } from 'react-multi-select-component'
import AddIcon from '@material-ui/icons/AddCircleOutlineOutlined'

import moment, { Moment } from 'config/moment'
import { MULTI_SELECT_STRINGS } from 'consts/strings'
import { Dialog, UploadButton, snackbar } from 'components'
import { useApiCall, useCheckFormErrors } from 'hooks'
import { PRIZE } from 'consts'
import { EditorInput } from 'features/Main/components'
import { StringUtils, ConfigUtils } from 'utils'
import { PrizeCategory } from 'consts/prize'
import { PartnerIntegration } from 'features/Main/components/PartnerIntegration'
import { CustomCheckbox } from 'components/CustomCheckbox'
import { CustomFormControlLabel } from 'components/CustomFormControlLabel'
import { Tag } from 'features/Main/Tags/types'
import { Membership } from 'features/Main/Memberships/types'
import { TabPanel } from 'components/Dialog/TabPanel'
import CustomTextField from 'components/CustomTextField'
import {
  TimeRestrictionConfig,
  TimeRestrictionConfig as TimeRestrictionConfigType,
} from 'components/TimeRestrictionConfig'

import { Prize, UpdatePrizeResponse, UpdatePrizeData } from '../../../../Prizes/types'
import { updatePrize } from '../../../../Prizes/api'
import { useStyles } from './styles'
import { REWARD_TYPE_BY_MEMBERSHIP } from '../constants'
import { PrizeDateRangeFields } from '../PrizeDateRangeFields'

interface ValidationData {
  toteatEnabled: boolean
  tucanCategory?: PrizeCategory
  toteatCategory?: PrizeCategory
}

const rules = {
  title: [
    {
      validate: (title: string) => StringUtils.extractTextFromHTML(title).length <= 70,
      message: 'Máximo 70 caracteres',
    },
  ],
  shortTitle: [
    {
      validate: (shortTitle: string) => shortTitle.length <= 40,
      message: 'Máximo 40 caracteres',
    },
  ],
  additionalText: [
    {
      validate: (value: string) => (value && value.length <= 100) || !value.length,
      message: 'Máximo 100 caracteres',
    },
  ],
  expirationDays: [
    {
      validate: (value: string) =>
        ((value && Number(value) >= 1) || !value) && Number.isInteger(Number(value)),
      message: 'El vencimiento debe ser un número entero mayor a 0',
    },
  ],
  toteatBenefitId: [
    {
      validate: (value: string, data: ValidationData) => {
        const { toteatEnabled, toteatCategory } = data
        if (toteatEnabled && toteatCategory === PrizeCategory.Checkout) {
          return value.trim().length > 0
        }
        return true
      },
      message: 'El ID de beneficio POS es requerido',
      validateEmpty: true,
    },
  ],
  skusList: [
    {
      validate: (skusList: string[], data: ValidationData) => {
        const { toteatEnabled, toteatCategory } = data
        if (toteatEnabled && toteatCategory === PrizeCategory.General) {
          return skusList.length > 0
        }
        return true
      },
      message: 'El SKU es requerido',
    },
  ],
}

interface UpdatePrizeDialogProps {
  handleClose: () => void
  prizeToUpdate: Prize
  onDone: (newPrize: Prize) => void
  tags: Tag[]
  memberships?: Membership[]
}

interface TagOption {
  label: string
  value: number
}

interface MembershipOption {
  label: string
  value: number
}

const UpdatePrizeDialog: React.FC<UpdatePrizeDialogProps> = ({
  handleClose,
  prizeToUpdate,
  onDone,
  tags,
  memberships = undefined,
}) => {
  const classes = useStyles()

  const [toteatBenefitId, setToteatBenefitId] = useState<string>(
    prizeToUpdate.toteatBenefitId || '',
  )
  const [currentSku, setCurrentSku] = useState<string>('')
  const [skusList, setSkusList] = useState<string[]>(
    prizeToUpdate.skus ? prizeToUpdate.skus.map(s => s.sku) : [],
  )
  const [storesEnabled, setStoresEnabled] = useState(prizeToUpdate.storesEnabled)
  const [tucanEnabled, setTucanEnabled] = useState(prizeToUpdate.tucanEnabled)
  const [toteatEnabled, setToteatEnabled] = useState(prizeToUpdate.toteatEnabled)
  const [tucanCategory, setTucanCategory] = useState<PrizeCategory | undefined>(
    prizeToUpdate.tucanCategory,
  )
  const [toteatCategory, setToteatCategory] = useState<PrizeCategory | undefined>(
    prizeToUpdate.toteatCategory,
  )
  const [title, setTitle] = useState(prizeToUpdate.title)
  const [shortTitle, setShortTitle] = useState(prizeToUpdate.shortTitle)
  const [icon, setIcon] = useState<File | undefined>(undefined)
  const [iconPreview, setIconPreview] = useState(prizeToUpdate.iconUrl)
  const [additionalText, setAdditionalText] = useState(
    prizeToUpdate.additionalText ? prizeToUpdate.additionalText : '',
  )
  const [rewardType, setrewardType] = useState(prizeToUpdate.rewardType)
  const [expirationDays, setExpirationDays] = useState<string>(
    prizeToUpdate.expirationDays ? prizeToUpdate.expirationDays.toString() : '',
  )
  const [startDate, setStartDate] = useState<Moment | null>(
    prizeToUpdate.startDate ? moment(prizeToUpdate.startDate) : null,
  )
  const [expiration, setExpiration] = useState<Moment | null>(
    prizeToUpdate.expiration ? moment(prizeToUpdate.expiration) : null,
  )

  const handleStartDateChange = (date: Moment | null) => {
    if (date) {
      setStartDate(date.startOf('day'))
    } else {
      setStartDate(null)
    }
  }

  const handleExpirationChange = (date: Moment | null) => {
    if (date) {
      setExpiration(date.endOf('day'))
    } else {
      setExpiration(null)
    }
  }
  const [allTagsAreRequired, setAllTagsAreRequired] = useState(prizeToUpdate.allTagsAreRequired)
  const [hasPartnerErrors, setHasPartnerErrors] = useState(false)
  const [tabIndex, setTabIndex] = useState(0)
  const [timeRestriction, setTimeRestriction] = useState<TimeRestrictionConfigType>({
    timeRestrictionEnabled: prizeToUpdate.timeRestrictionEnabled || false,
    allowedDays: prizeToUpdate.allowedDays || [],
    startTime: prizeToUpdate.startTime || '08:00',
    endTime: prizeToUpdate.endTime || '18:00',
  })
  const [tagsSelected, setTagsSelected] = useState<TagOption[]>([])
  const [membershipsSelected, setMembershipsSelected] = useState<MembershipOption[]>([])

  const tagsForSelect = tags.map(el => ({ label: el.title, value: el.id }))
  const membershipsForSelect: MembershipOption[] =
    memberships?.map(el => ({ label: el.title, value: el.id })) || []

  const handleSetTags = async (e: TagOption[]) => {
    setTagsSelected(e)
  }

  const handleSetMemberships = async (e: MembershipOption[]) => {
    setMembershipsSelected(e)
  }

  const handleAllTagsAreRequiredChange: React.ChangeEventHandler<HTMLInputElement> = () => {
    setAllTagsAreRequired(!allTagsAreRequired)
  }

  useEffect(() => {
    if (!prizeToUpdate) {
      setTagsSelected([])
      setMembershipsSelected([])
      return
    }

    // Initialize tags
    if (prizeToUpdate.tags && Array.isArray(prizeToUpdate.tags) && prizeToUpdate.tags.length > 0) {
      const localTagsSelected = prizeToUpdate.tags.map(el => ({
        label: el.title,
        value: el.id,
      }))
      setTagsSelected(localTagsSelected)
    } else {
      setTagsSelected([])
    }

    // Initialize memberships
    if (
      prizeToUpdate.memberships &&
      Array.isArray(prizeToUpdate.memberships) &&
      prizeToUpdate.memberships.length > 0
    ) {
      const localMembershipsSelected = prizeToUpdate.memberships.map(el => ({
        label: el.title,
        value: el.id,
      }))
      setMembershipsSelected(localMembershipsSelected)
    } else {
      setMembershipsSelected([])
    }
  }, [prizeToUpdate])

  const [updateTagApi, isLoading] = useApiCall<UpdatePrizeData, UpdatePrizeResponse>(updatePrize)

  const getRequiredFields = () => {
    const requiredFields = [
      'title',
      'shortTitle',
      'additionalText',
      ...(rewardType === PRIZE.REWARD_TYPE.AUTOMATIC ? ['expirationDays'] : []),
    ]

    if (toteatEnabled) {
      if (toteatCategory === PrizeCategory.General) {
        requiredFields.push('skusList')
      } else if (toteatCategory === PrizeCategory.Checkout) {
        requiredFields.push('toteatBenefitId')
      }
    }

    return requiredFields
  }

  const fields = {
    title,
    shortTitle,
    additionalText,
    expirationDays,
    toteatEnabled,
    toteatBenefitId,
    skusList,
    tucanCategory,
    toteatCategory,
  }
  const { hasErrors, isAnyFieldEmpty, errors } = useCheckFormErrors(
    fields,
    rules,
    getRequiredFields(),
  )
  const isSubmitDisabled = hasErrors || isAnyFieldEmpty || isLoading || hasPartnerErrors

  const handleIconChange = (file: File) => {
    setIconPreview(URL.createObjectURL(file))
    setIcon(file)
  }

  const handlerewardTypeSelect = (event: React.ChangeEvent<{ value: unknown }>) => {
    setrewardType(event.target.value as string)
  }

  const onUpdateTag = async () => {
    const data = {
      title,
      shortTitle,
      icon,
      additionalText,
      rewardType,
      expirationDays,
      ...(startDate ? { startDate: startDate.toISOString() } : {}),
      ...(expiration ? { expiration: expiration.toISOString() } : {}),
      tucanCategory,
      toteatCategory,
      storesEnabled,
      tucanEnabled,
      toteatEnabled,
      skus: skusList,
      toteatBenefitId,
      tagsSelected,
      membershipsSelected,
      allTagsAreRequired,
      timeRestriction,
    }

    const payload = {
      id: prizeToUpdate.id,
      payload: data,
    }
    try {
      const { prize } = await updateTagApi(payload)
      onDone(prize)
    } catch (err) {
      console.error(err)
      snackbar.show('No se pudo editar el premio. Intente de nuevo.')
    }
  }

  const handlePartnerValidationChange = (hasErrorsPartner: boolean) => {
    setHasPartnerErrors(hasErrorsPartner)
  }

  const hasAnyIntegration = ConfigUtils.hasAnyIntegrationEnabled()
  const isEcardEnabled = process.env.REACT_APP_ECARD_ENABLED === 'true'
  const tabs = ['Descripción', 'Asignación', 'Horarios']

  const isMembershipRequired = membershipsSelected.length === 0
  const isSubmitDisabledFinal = isSubmitDisabled || isMembershipRequired

  return (
    <Dialog
      title="Editar premio"
      isOpen
      showTabsActions
      onCancel={handleClose}
      isLoading={isLoading}
      okButtonText="Editar"
      okButtonProps={{ disabled: isSubmitDisabledFinal, startIcon: <AddIcon /> }}
      onAccept={onUpdateTag}
      contentStyle={{ minWidth: 500 }}
      overflow
      style={{
        minWidth: 600,
        minHeight: 640,
        borderRadius: 15,
      }}
      tabs={tabs}
      tabIndex={tabIndex}
      setTabIndex={setTabIndex}
    >
      <TabPanel value={tabIndex} index={0} id="description">
        <Grid container>
          <Grid item xs={12}>
            <FormControl className={classes.fullWidth}>
              <InputLabel id="demo-simple-select-label" className={classes.inputLabel}>
                Tipo de premio
              </InputLabel>
              <Select
                className={classes.inputLabel}
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={rewardType}
                onChange={handlerewardTypeSelect}
                fullWidth
              >
                {REWARD_TYPE_BY_MEMBERSHIP.map(value => (
                  <MenuItem className={classes.menuItem} value={value.id} key={value.id}>
                    {value.description}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <EditorInput
              label="Título"
              value={title}
              readOnly={isLoading}
              required
              onChange={setTitle}
              maxLength={70}
              error={errors.title.hasError}
              errorText={errors.title.message}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomTextField
              label="Título secundario"
              value={additionalText}
              fullWidth
              onChange={e => setAdditionalText(e.target.value)}
              disabled={isLoading}
              required
              maxLength={100}
              error={errors.additionalText.hasError}
              helperText={
                errors.additionalText.hasError ? errors.additionalText.message : undefined
              }
            />
          </Grid>
          <Grid item xs={12}>
            <CustomTextField
              label="Descripción"
              value={shortTitle}
              fullWidth
              onChange={e => setShortTitle(e.target.value)}
              disabled={isLoading}
              required
              maxLength={40}
              error={errors.shortTitle.hasError}
              helperText={errors.shortTitle.hasError ? errors.shortTitle.message : undefined}
            />
          </Grid>

          {rewardType === PRIZE.REWARD_TYPE.AUTOMATIC && (
            <>
              <Grid item xs={12} sm={12}>
                <TextField
                  value={expirationDays}
                  type="number"
                  fullWidth
                  onChange={e => setExpirationDays(e.target.value)}
                  disabled={isLoading}
                  label="Vencimiento"
                  required
                  InputProps={{
                    inputProps: { min: 1 },
                    endAdornment: <InputAdornment position="start">días</InputAdornment>,
                  }}
                  error={errors.expirationDays.hasError}
                  helperText={errors.expirationDays.message}
                />
              </Grid>
            </>
          )}

          <Grid container xs={12} className={classes.iconUploadContainer}>
            <Grid item xs={6} className={classes.iconUploadButton}>
              <UploadButton
                id="manualReward-icon-upload-button"
                accept=".jpg, .jpeg, .png"
                label="Subir ícono premio *"
                onChange={handleIconChange}
                maxSizeInMB={2}
              />
            </Grid>
            <Grid item xs={6}>
              <div className={classes.iconUploadPreviewContainer}>
                <img src={iconPreview} alt="" className={classes.iconPreview} />
              </div>
            </Grid>
          </Grid>

          <PrizeDateRangeFields
            startDate={startDate}
            expiration={expiration}
            onStartDateChange={handleStartDateChange}
            onExpirationChange={handleExpirationChange}
            disabled={isLoading}
            enforceStartNotBeforeToday={false}
          />

          {hasAnyIntegration && (
            <>
              <Grid item xs={12}>
                <Typography className={classes.dialogSubTitle} style={{ marginTop: 16 }}>
                  Integraciones
                </Typography>
              </Grid>
              <div style={{ marginBottom: 24 }}>
                <PartnerIntegration
                  hideTitle
                  storesEnabled={storesEnabled}
                  setStoresEnabled={setStoresEnabled}
                  tucanEnabled={tucanEnabled}
                  setTucanEnabled={setTucanEnabled}
                  toteatEnabled={toteatEnabled}
                  setToteatEnabled={setToteatEnabled}
                  tucanCategory={tucanCategory}
                  setTucanCategory={setTucanCategory}
                  toteatCategory={toteatCategory}
                  setToteatCategory={setToteatCategory}
                  currentSku={currentSku}
                  setCurrentSku={setCurrentSku}
                  skusList={skusList}
                  setSkusList={setSkusList}
                  toteatBenefitId={toteatBenefitId}
                  setToteatBenefitId={setToteatBenefitId}
                  errors={errors}
                  isLoading={isLoading}
                  onValidationChange={handlePartnerValidationChange}
                />
              </div>
            </>
          )}
        </Grid>
      </TabPanel>

      <TabPanel value={tabIndex} index={1} id="filters">
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography className={classes.dialogSubTitle}>Niveles</Typography>
            <FormControl className={classes.fullWidth}>
              <MultiSelect
                options={membershipsForSelect}
                value={membershipsSelected}
                onChange={handleSetMemberships}
                labelledBy="Seleccionar Niveles"
                hasSelectAll={false}
                className={classes.outlinedMultiSelect}
                overrideStrings={MULTI_SELECT_STRINGS}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Typography className={classes.dialogSubTitle}>Tags</Typography>
            <FormControl className={classes.fullWidth}>
              <MultiSelect
                options={tagsForSelect}
                value={tagsSelected}
                onChange={handleSetTags}
                labelledBy="Seleccionar Tags"
                hasSelectAll={false}
                disabled
                className={`${classes.outlinedMultiSelect} disabled`}
                overrideStrings={MULTI_SELECT_STRINGS}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <CustomFormControlLabel
              control={
                <CustomCheckbox
                  onChange={handleAllTagsAreRequiredChange}
                  checked={allTagsAreRequired}
                  disabled
                />
              }
              label="Sólo usuarios con todos los tags seleccionados"
            />
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabIndex} index={2} id="time-restriction">
        <TimeRestrictionConfig
          config={timeRestriction}
          onChange={setTimeRestriction}
          disabled={isLoading}
          hideTimeSelectors={isEcardEnabled}
        />
      </TabPanel>
    </Dialog>
  )
}

UpdatePrizeDialog.defaultProps = {
  memberships: undefined,
}

export { UpdatePrizeDialog }
